import type { GameMode } from "../../GameMode.js";
import type { PlayerNo } from "../../PlayerNo.js";
import type { BotType } from "../../BotType.js";
import type { GameEndReason } from "../../GameEndReason.js";
import type { PlayerType } from "../../../../royalur/model/PlayerType.js";
import type { RemoteLobbyStatus } from "./RemoteLobbyStatus.js";
import type { LocalLobbyStatus } from "./LocalLobbyStatus.js";
import type { AnalysisLobbyStatus } from "./AnalysisLobbyStatus.js";
import type { LobbyID } from "../../../lobby/LobbyID.js";
import { assertNever } from "../../../../util/tsutils.js";


export abstract class LobbyStatus {
    abstract isRemote(): this is RemoteLobbyStatus;

    abstract isLocal(): this is LocalLobbyStatus;

    abstract isAnalysis(): this is AnalysisLobbyStatus;

    /**
     * Returns an ID that can be used to uniquely refer to this game.
     * Has different uniqueness constraints depending on the source
     * of the ID.
     */
    abstract getLobbyID(): LobbyID;

    abstract hasEverConnected(): boolean;

    abstract isConnected(): boolean;

    abstract isGameAvailable(): boolean;

    abstract isGameUnknown(): boolean;

    abstract getGameMode(): GameMode | null;

    abstract getBotType(): BotType | null;

    abstract getEndReason(): GameEndReason | null;

    abstract getEndingPlayer(): PlayerType | null;

    abstract hasPingTimes(): boolean;

    abstract getPlayer1LastPingTimeMs(): number;

    abstract getPlayer2LastPingTimeMs(): number;

    getPlayerLastPingTimeMs(playerNo: PlayerNo): number {
        if (playerNo === 1)
            return this.getPlayer1LastPingTimeMs();
        if (playerNo === 2)
            return this.getPlayer2LastPingTimeMs();

        return assertNever(playerNo);
    }

    abstract getCompleteGameID(): string | null;

    abstract withCompleteGameID(completeGameID: string | null): LobbyStatus;

    abstract withEnd(endReason: GameEndReason, endingPlayer: PlayerType): LobbyStatus;

    abstract getRematchRequestedBy(): PlayerNo | null;

    asRemote(): RemoteLobbyStatus {
        if (this.isRemote())
            return this;

        throw new Error(`Expected RemoteLobbyStatus but got ${this.constructor.name}`);
    }

    asLocal(): LocalLobbyStatus {
        if (this.isLocal())
            return this;

        throw new Error(`Expected LocalLobbyStatus but got ${this.constructor.name}`);
    }

    asAnalysis(): AnalysisLobbyStatus {
        if (this.isAnalysis())
            return this;

        throw new Error(`Expected AnalysisLobbyStatus but got ${this.constructor.name}`);
    }
}
